"use client";

import { useChordLabel } from "@/hooks/use-notation";
import { useMemo } from "react";
import {
  FONT_MONO,
  TEXT_MUTE,
  VIOLET_BRIGHT,
} from "@/components/ui/organisms/chord-practice/palette";
import {
  formatAccidentals,
  theoreticalPitchClasses,
} from "@/lib/music/theory-helpers";
import { usePressedPitchClasses } from "@/hooks/use-pressed-pitch-classes";
import { parseChordSymbol } from "@/lib/piece-overview/parse-chord-symbol";
import { chordRomanNumeral } from "@/lib/piece-overview/chord-roman-numeral";
import { defaultProgression } from "@/lib/piece-overview/default-progression";
import type { PieceTonality } from "@/lib/piece-overview/piece-overview-data";

interface PieceProgressionInlineProps {
  tonality: PieceTonality | null;
  signatureProgression: ReadonlyArray<string> | null;
  onOpenHarmony: (() => void) | null;
}

type ProgressionItem = {
  symbol: string;
  roman: string | null;
  pitchClasses: number[] | null;
};

/**
 * Minimal chord progression strip rendered inline below the hero.
 * Shows the analyzer's signature progression when available, falling
 * back to the top-N most frequent chords. For each chord we render a
 * roman numeral above the symbol when the tonality is known and the
 * chord lives within the diatonic gamme, chromatic / unrecognized
 * chords still display their symbol but no roman.
 *
 * Chips light up when the player presses every pitch class of the
 * chord (octave-agnostic). Activation is a soft glow on the roman to
 * stay coherent with the immersive "Souffle" direction, no boxes.
 */
export function PieceProgressionInline({
  tonality,
  signatureProgression,
  onOpenHarmony,
}: PieceProgressionInlineProps) {
  const pressed = usePressedPitchClasses();

  const { items, isDefault } = useMemo<{
    items: ProgressionItem[];
    isDefault: boolean;
  }>(() => {
    // Source priority : analyzer's signature progression first, then
    // fall back to the didactic I-IV-V-I cadence of the detected key.
    // If we don't even have a tonality, render nothing.
    const fromSignature =
      signatureProgression !== null && signatureProgression.length > 0
        ? signatureProgression
        : null;

    const fallback =
      fromSignature === null && tonality !== null
        ? defaultProgression(tonality)
        : null;

    const rawSymbols = fromSignature ?? fallback ?? [];

    const symbols = rawSymbols.filter(
      (s): s is string => typeof s === "string" && s.length > 0,
    );

    const built = symbols.map((symbol) => {
      const parsed = parseChordSymbol(symbol);

      if (!parsed) {
        return { symbol, roman: null, pitchClasses: null };
      }

      const pcs = theoreticalPitchClasses(parsed.root, parsed.quality);
      const roman =
        tonality !== null ? chordRomanNumeral(parsed, tonality) : null;

      return {
        symbol,
        roman,
        pitchClasses: pcs.length > 0 ? pcs : null,
      };
    });

    return { items: built, isDefault: fromSignature === null };
  }, [signatureProgression, tonality]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
      }}
    >
      {isDefault ? (
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 9,
            color: TEXT_MUTE,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          Cadence parfaite · progression de référence
        </div>
      ) : null}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "center",
          gap: 18,
          flexWrap: "wrap",
        }}
      >
      {items.map((item, idx) => {
        const active =
          item.pitchClasses !== null &&
          pressed.size > 0 &&
          item.pitchClasses.every((pc) => pressed.has(pc));

        return (
          <ChordCellGroup
            key={`${item.symbol}-${idx}`}
            item={item}
            active={active}
            isLast={idx === items.length - 1}
          />
        );
      })}
      {onOpenHarmony !== null ? (
        <button
          type="button"
          onClick={onOpenHarmony}
          style={{
            marginLeft: 14,
            background: "transparent",
            border: "none",
            padding: 0,
            fontSize: 11,
            color: VIOLET_BRIGHT,
            opacity: 0.7,
            cursor: "pointer",
            fontFamily: "Inter, sans-serif",
            transition: "opacity 150ms",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "0.7";
          }}
        >
          Explorer →
        </button>
      ) : null}
      </div>
    </div>
  );
}

function ChordCellGroup({
  item,
  active,
  isLast,
}: {
  item: ProgressionItem;
  active: boolean;
  isLast: boolean;
}) {
  const chordLabel = useChordLabel();

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: 18,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 20,
            fontWeight: 300,
            color: active ? "#fff" : VIOLET_BRIGHT,
            textShadow: active ? `0 0 12px rgba(196,181,253,0.7)` : "none",
            transition: "color 120ms, text-shadow 120ms",
            minWidth: 18,
            textAlign: "center",
          }}
        >
          {item.roman ?? chordLabel(item.symbol)}
        </span>
        {item.roman !== null ? (
          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: 10,
              color: TEXT_MUTE,
            }}
          >
            {chordLabel(item.symbol)}
          </span>
        ) : null}
      </span>
      {!isLast ? (
        <span
          aria-hidden
          style={{
            color: TEXT_MUTE,
            fontSize: 14,
            fontFamily: FONT_MONO,
            opacity: 0.6,
          }}
        >
          ·
        </span>
      ) : null}
    </span>
  );
}

